import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../../../lib/auth-context.js';
import { makeCustomFormsApi, type CustomFormsApi } from './api.js';
import { resolveActionError, toCustomFormModel } from './actions.js';
import type {
  WorkfrontCustomFormItem,
  WorkfrontCustomFormModel,
  WorkfrontWorkspaceOption,
} from './types.js';

/**
 * TanStack-flavored data hooks for the custom-forms migration wizard.
 * Wraps the operations exposed by `api.ts` with React Query so cache
 * invalidation lines up with the rest of the SPA.
 *
 *   `useCustomFormsApi` — singleton-per-render API client
 *   `useCustomForms(workspaceRef)` — list forms on a workspace
 *   `useCustomFormDetails(workspaceRef, formId)` — load one form's
 *      parameters + groups, transformed into the legacy
 *      `WorkfrontCustomFormModel` shape
 *   `useActionLog()` — console-style progress log for the clone flow
 */

export function useCustomFormsApi(): CustomFormsApi {
  const { api } = useAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => makeCustomFormsApi(api), [api]);
}

interface PanelState {
  workspaceRef: string;
  formId: string | null;
}

export function useCustomForms(workspaceRef: string): {
  forms: ReadonlyArray<WorkfrontCustomFormItem>;
  isLoading: boolean;
  error: Error | null;
} {
  const api = useCustomFormsApi();
  const { session } = useAuth();

  const q = useQuery({
    queryKey: ['workfront', 'custom-forms', 'list', session?.licenseId ?? 'anon', workspaceRef],
    queryFn: async () => {
      const response = await api.getCustomForms({ workspaceRef });
      if (response.error) throw new Error(response.error.message);
      return response.data ?? [];
    },
    enabled: !!session && !!workspaceRef,
    staleTime: 60_000,
  });

  return {
    forms: q.data ?? [],
    isLoading: q.isLoading || q.isFetching,
    error: (q.error as Error | null) ?? null,
  };
}

export function useCustomFormDetails(panel: PanelState): {
  formDetails: WorkfrontCustomFormModel | null;
  isLoading: boolean;
  error: Error | null;
} {
  const api = useCustomFormsApi();
  const { session } = useAuth();

  const q = useQuery({
    queryKey: [
      'workfront',
      'custom-forms',
      'details',
      session?.licenseId ?? 'anon',
      panel.workspaceRef,
      panel.formId,
    ],
    queryFn: async () => {
      const response = await api.getCustomFormDetails(
        { workspaceRef: panel.workspaceRef },
        panel.formId!,
      );
      if (response.error || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch custom form details');
      }
      return toCustomFormModel(response.data);
    },
    enabled: !!session && !!panel.workspaceRef && !!panel.formId,
    staleTime: 60_000,
  });

  return {
    formDetails: q.data ?? null,
    isLoading: q.isLoading || q.isFetching,
    error: (q.error as Error | null) ?? null,
  };
}

export function useActionLog(): {
  logs: ReadonlyArray<string>;
  addLog: (message: string) => void;
  clearLogs: () => void;
} {
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, `[${timestamp}] ${message}`]);
  }, []);

  const clearLogs = useCallback(() => setLogs([]), []);

  return { logs, addLog, clearLogs };
}

export function useFilteredWorkspaceOptions(
  workspaceOptions: ReadonlyArray<WorkfrontWorkspaceOption>,
  excludeRef: string,
): ReadonlyArray<WorkfrontWorkspaceOption> {
  return useMemo(
    () => workspaceOptions.filter((opt) => opt.value !== excludeRef),
    [workspaceOptions, excludeRef],
  );
}

export { resolveActionError };
